import { Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FieldHelp } from '@/features/guided-help';
import { Gender } from '@/types/tournament.model';
import { cn } from '@/lib/utils';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

const GENDER_OPTIONS: Record<Gender, string> = {
  [Gender.MALE]: 'Masculino',
  [Gender.FEMALE]: 'Femenino',
  [Gender.MIXED]: 'Mixto',
  [Gender.ANY]: 'Abierto',
};

export interface GenderFieldProps {
  value: Gender;
  onChange: (gender: Gender) => void;
  wrapInCard?: boolean;
  disabled?: boolean;
}

export function GenderField({
  value,
  onChange,
  wrapInCard = true,
  disabled = false,
}: GenderFieldProps) {
  const handleChange = (next: string) => {
    onChange(next as Gender);
  };

  const inner = (
    <RadioGroup
      value={value}
      onValueChange={handleChange}
      disabled={disabled}
      className="grid grid-cols-2 gap-4 sm:grid-cols-4"
    >
      {Object.entries(GENDER_OPTIONS).map(([optionValue, label]) => (
        <GenderOption
          key={optionValue}
          value={optionValue}
          label={label}
          isSelected={value === optionValue}
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );

  if (!wrapInCard) {
    return (
      <div className={wizardBlockShellClassName} data-guide-id="wizard.team.gender">
        {inner}
      </div>
    );
  }

  return (
    <Card className={wizardBlockCardClassName} data-guide-id="wizard.team.gender">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" />
          Género del torneo
          <FieldHelp topicId="wizard.team.gender" />
        </CardTitle>
      </CardHeader>
      <CardContent>{inner}</CardContent>
    </Card>
  );
}

interface GenderOptionProps {
  value: string;
  label: string;
  isSelected: boolean;
  disabled?: boolean;
}

function GenderOption({
  value,
  label,
  isSelected,
  disabled,
}: GenderOptionProps) {
  return (
    <Label
      htmlFor={`gender-${value}`}
      className={cn(
        'flex cursor-pointer items-center justify-center rounded-lg border-2 p-4 transition-all',
        disabled && 'cursor-not-allowed opacity-60',
        isSelected
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-gray-200 hover:border-gray-300',
      )}
    >
      <RadioGroupItem
        value={value}
        id={`gender-${value}`}
        className="sr-only"
        disabled={disabled}
      />
      <span className="font-medium">{label}</span>
    </Label>
  );
}
